import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Wrench, Video, Megaphone, Calendar } from 'lucide-react';
import YouTubeScraperCard from './admin/YouTubeScraperCard';
import FacebookCard from './admin/FacebookCard';
import ScheduleSettingsDialog from './ScheduleSettingsDialog';

/**
 * ToolsPanel — top-level "Tools" tab (Tech Specialist / System Admin).
 * A multi-tab home for utility tools. First tab = Media Scraper (the YouTube
 * media scraper relocated from the Website Visibility page). Add more tools by
 * appending to TOOL_TABS.
 */
const TOOL_TABS = [
  { key: 'media-scraper', label: 'Media Scraper', icon: Video },
  { key: 'facebook', label: 'Facebook', icon: Megaphone },
  // The Scheduler's Settings → Google Calendars pane, mirrored (same component).
  { key: 'google-calendars', label: 'Google Calendars', icon: Calendar },
];

export default function ToolsPanel({ orgId }) {
  const { isDark } = useTheme();
  const [active, setActive] = useState(TOOL_TABS[0].key);
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="space-y-4" data-testid="tools-panel">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-teal-500/15"><Wrench className="h-5 w-5 text-teal-400" /></div>
        <div>
          <h2 className="text-lg font-semibold">Tools</h2>
          <p className={`text-sm ${muted} mt-0.5`}>
            Utility tools for managing your site's content and media.
          </p>
        </div>
      </div>

      {/* Sub-tab bar */}
      <div className={`flex flex-wrap gap-1 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
        {TOOL_TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActive(t.key)}
              data-testid={`tools-subtab-${t.key}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
                isActive
                  ? 'border-teal-500 text-teal-500'
                  : `border-transparent ${muted} hover:text-teal-400`
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div>
        {active === 'media-scraper' && (
          <div data-testid="tools-tab-media-scraper">
            <YouTubeScraperCard orgId={orgId} />
          </div>
        )}
        {active === 'facebook' && (
          <div data-testid="tools-tab-facebook">
            <FacebookCard orgId={orgId} />
          </div>
        )}
        {active === 'google-calendars' && (
          <div data-testid="tools-tab-google-calendars">
            <ScheduleSettingsDialog orgId={orgId} open embedded isDark={isDark} />
          </div>
        )}
      </div>
    </div>
  );
}
